import React, { useState, useRef, useEffect } from "react";
import { useDarkMode } from "../../contexts/DarkModeContext";
import {
  ImageUploadComponent,
  ValidationRule,
} from "../../services/types/ComponentTypes";

interface ImageUploadProps {
  config: ImageUploadComponent;
  value?: string | string[]; // Can be a single base64/URL or an array
  onChange?: (name: string, value: string | string[] | null) => void;
  onUpload?: (files: File[]) => void;
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
  config,
  value,
  onChange,
  onUpload,
}) => {
  const [error, setError] = useState<string>("");
  const [isDragOver, setIsDragOver] = useState(false);
  const [previews, setPreviews] = useState<string[]>([]);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewingImageIndex, setViewingImageIndex] = useState<number | null>(null);
  const [zoomLevel, setZoomLevel] = useState(100);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const { isDarkMode } = useDarkMode();

  // Convert incoming value to array for internal handling
  const values = Array.isArray(value) ? value : value ? [value] : [];

  useEffect(() => {
    // Sync previews with incoming base64/URL values
    const newValues = Array.isArray(value) ? value : value ? [value] : [];
    setPreviews(newValues);
  }, [value]);

  // Cleanup camera stream on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };
  }, []);

  const validateFiles = (files: File[]): string => {
    if (!config.validation) return "";

    for (const rule of config.validation) {
      switch (rule.rule) {
        case ValidationRule.REQUIRED:
          if (files.length === 0 && values.length === 0)
            return rule.message || "Please upload at least one file";
          break;
      }
    }

    if (config.maxFiles && (files.length + values.length) > config.maxFiles) {
      return `Maximum ${config.maxFiles} file${config.maxFiles > 1 ? "s" : ""
        } allowed`;
    }

    for (const file of files) {
      if (config.maxFileSize && file.size > config.maxFileSize * 1024 * 1024) {
        return `File "${file.name}" exceeds maximum size of ${config.maxFileSize}MB`;
      }

      if (
        config.acceptedFileTypes &&
        !config.acceptedFileTypes.includes(file.type)
      ) {
        return `File "${file.name
          }" type not supported. Accepted types: ${config.acceptedFileTypes.join(
            ", "
          )}`;
      }
    }

    return "";
  };

  const handleFileChange = async (files: File[]) => {
    const validationError = validateFiles(files);
    setError(validationError);

    if (!validationError && files.length > 0) {
      const newBase64s: string[] = [];

      for (const file of files) {
        const base64 = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target?.result as string);
          reader.readAsDataURL(file);
        });
        newBase64s.push(base64);
      }

      const updatedValues = config.maxFiles === 1 ? [newBase64s[0]] : [...values, ...newBase64s];

      // Update form state
      if (config.maxFiles === 1) {
        onChange?.(config.name, updatedValues[0]);
      } else {
        onChange?.(config.name, updatedValues);
      }

      if (config.uploadAction && onUpload) {
        onUpload(files);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    handleFileChange(files);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    handleFileChange(files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleClick = () => {
    if (!config.disabled) {
      fileInputRef.current?.click();
    }
  };

  const startCameraCapture = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' }, 
        audio: false 
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setIsCapturing(true);
      }
    } catch (error) {
      console.error("Error accessing camera:", error);
      setError("Unable to access camera. Please check permissions.");
    }
  };

  const stopCameraCapture = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsCapturing(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;

    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');
    
    if (ctx) {
      ctx.drawImage(videoRef.current, 0, 0);
      const base64 = canvas.toDataURL('image/jpeg', 0.9);
      
      // Stop camera
      stopCameraCapture();
      
      // Handle the captured image
      const updatedValues = config.maxFiles === 1 ? [base64] : [...values, base64];
      
      if (config.maxFiles === 1) {
        onChange?.(config.name, updatedValues[0]);
      } else {
        onChange?.(config.name, updatedValues);
      }
    }
  };

  const removeFile = (index: number) => {
    // Use current previews state for removal
    const currentPreviews = previews.length > 0 ? previews : values;
    const newValues = currentPreviews.filter((_, i) => i !== index);
    
    // Update local previews state immediately
    setPreviews(newValues);

    // Close view modal if viewing the removed image
    if (viewingImageIndex === index) {
      setIsViewModalOpen(false);
      setViewingImageIndex(null);
      setZoomLevel(100);
    } else if (viewingImageIndex !== null && viewingImageIndex > index) {
      // Adjust viewing index if a previous image was removed
      setViewingImageIndex(viewingImageIndex - 1);
    }

    if (newValues.length === 0) {
      onChange?.(config.name, null);
    } else {
      if (config.maxFiles === 1) {
        onChange?.(config.name, newValues[0]);
      } else {
        onChange?.(config.name, newValues);
      }
    }
  };

  const handleViewImage = (index: number) => {
    setViewingImageIndex(index);
    setIsViewModalOpen(true);
    setZoomLevel(100);
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setViewingImageIndex(null);
    setZoomLevel(100);
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 25, 300));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 25, 25));
  };

  const handleResetZoom = () => {
    setZoomLevel(100);
  };

  const handleNextImage = () => {
    if (viewingImageIndex !== null && viewingImageIndex < previews.length - 1) {
      setViewingImageIndex(viewingImageIndex + 1);
      setZoomLevel(100);
    }
  };

  const handlePreviousImage = () => {
    if (viewingImageIndex !== null && viewingImageIndex > 0) {
      setViewingImageIndex(viewingImageIndex - 1);
      setZoomLevel(100);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getWidthClass = () => {
    if (config.style?.widthMode === "wrap") return "w-auto";
    return "w-full";
  };

  const containerStyles: React.CSSProperties = {
    margin: config.style?.margin || 0,
    padding: config.style?.padding || 0,
  };

  // For single file uploads, hide upload area when file is already uploaded
  const isSingleFile = config.maxFiles === 1;
  const hasFile = previews.length > 0;
  const showUploadArea = !isSingleFile || !hasFile;

  return (
    <div className={`${getWidthClass()} px-2`} style={containerStyles}>
      {config.label && (
        <label className="block mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
          {config.label}
          {config.required && <span className="text-red-500 dark:text-red-400 ml-1">*</span>}
        </label>
      )}

      {/* Camera Capture Modal */}
      {isCapturing && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-75 dark:bg-opacity-85">
          <div className="relative w-full max-w-2xl bg-white dark:bg-gray-800 rounded-lg shadow-2xl p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Capture Photo
              </h3>
              <button
                onClick={stopCameraCapture}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title="Close"
              >
                <svg className="w-5 h-5 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="relative bg-black rounded-lg overflow-hidden mb-4">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                className="w-full h-auto max-h-[60vh]"
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={stopCameraCapture}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={capturePhoto}
                className="px-4 py-2 bg-primary-500 dark:bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-600 dark:hover:bg-primary-700 transition-colors"
              >
                Capture
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Area - Only show when no file is uploaded (for single file) or always (for multiple files) */}
      {showUploadArea && (
        <div className="space-y-2">
          <div
            className={`group relative border-2 border-dashed text-center transition-all duration-300 min-h-[120px] flex flex-col items-center justify-center gap-2 rounded-lg overflow-hidden p-6 ${
              config.disabled 
                ? "cursor-not-allowed opacity-60" 
                : "cursor-pointer hover:shadow-md"
            } ${
              error
                ? "border-red-400 dark:border-red-500 bg-red-50 dark:bg-red-900/20"
                : isDragOver
                  ? "border-primary-500 dark:border-primary-400 bg-primary-50 dark:bg-primary-900/30 shadow-md scale-[1.01]"
                  : "border-gray-300 dark:border-gray-600 bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 hover:border-primary-400 dark:hover:border-primary-500"
            }`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={handleClick}
          >
          {/* Animated background gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary-50/0 via-primary-50/0 to-primary-50/0 dark:from-primary-900/0 dark:via-primary-900/0 dark:to-primary-900/0 group-hover:from-primary-50/30 group-hover:via-primary-50/20 group-hover:to-primary-50/10 dark:group-hover:from-primary-900/20 dark:group-hover:via-primary-900/10 dark:group-hover:to-primary-900/5 transition-all duration-500" />
          
          {/* Modern upload icon */}
          <div className="relative z-10 flex flex-col items-center justify-center">
            <div className={`relative mb-2 transition-transform duration-300 ${isDragOver ? "scale-110" : "group-hover:scale-105"}`}>
              <svg 
                className="w-12 h-12 text-primary-500 dark:text-primary-400 transition-colors duration-300" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={1.5} 
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" 
                />
              </svg>
              {/* Animated upload arrow */}
              {isDragOver && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-6 h-6 border-2 border-primary-500 rounded-full border-t-transparent animate-spin" />
                </div>
              )}
            </div>
            
            <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-0.5 transition-colors duration-200 group-hover:text-primary-600 dark:group-hover:text-primary-400">
              {config.placeholder || "Click to upload or drag and drop"}
            </div>
            
            <div className="flex flex-wrap items-center justify-center gap-2 text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {config.acceptedFileTypes && (
                <span className="flex items-center gap-1">
                  <span className="font-medium">Accepted:</span>
                  <span className="text-primary-600 dark:text-primary-400">
                    {config.acceptedFileTypes
                      .map((type) => type.split("/")[1].toUpperCase())
                      .join(", ")}
                  </span>
                </span>
              )}
              {config.maxFileSize && (
                <span className="flex items-center gap-1">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                  </svg>
                  <span>Max: <strong>{config.maxFileSize}MB</strong></span>
                </span>
              )}
              {config.maxFiles && config.maxFiles > 1 && (
                <span className="flex items-center gap-1">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Max: <strong>{config.maxFiles}</strong> files</span>
                </span>
              )}
            </div>
          </div>
        </div>
          
          {/* Camera Capture Button */}
          {!config.disabled && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                startCameraCapture();
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary-50 dark:bg-primary-900/30 border border-primary-300 dark:border-primary-700 text-primary-600 dark:text-primary-400 rounded-lg text-sm font-medium hover:bg-primary-100 dark:hover:bg-primary-900/50 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Capture from Camera
            </button>
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        multiple={config.maxFiles !== 1}
        accept={config.acceptedFileTypes?.join(",")}
        onChange={handleInputChange}
        className="hidden"
        disabled={config.disabled}
      />

      {/* Preview Section - Compact design */}
      {config.previewEnabled && previews.length > 0 && (
        <div className={isSingleFile ? "mt-3" : "mt-4"}>
          {!isSingleFile && (
            <div className="mb-3 flex items-center justify-between">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Uploaded Images
              </h4>
              <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full">
                {previews.length} {previews.length === 1 ? 'file' : 'files'}
              </span>
            </div>
          )}
          <div className={isSingleFile ? "" : "grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-4"}>
            {previews.map((preview, index) => (
              <div
                key={index}
                className={`group relative rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm hover:shadow-md transition-all duration-200 hover:border-primary-400 dark:hover:border-primary-500 ${
                  isSingleFile ? "flex items-center gap-3 p-3" : ""
                }`}
              >
                {isSingleFile ? (
                  <>
                    {/* Compact horizontal layout for single file */}
                    <div className="relative w-20 h-20 flex-shrink-0 rounded-md overflow-hidden bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
                      <img
                        src={preview}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate">
                        Image {index + 1}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {config.acceptedFileTypes?.[0]?.split("/")[1].toUpperCase() || "Image"}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewImage(index);
                        }}
                        className="flex-shrink-0 bg-primary-50 dark:bg-primary-900/30 border border-primary-300 dark:border-primary-700 text-primary-600 dark:text-primary-400 rounded-md px-3 py-1.5 text-xs font-medium hover:bg-primary-100 dark:hover:bg-primary-900/50 transition-colors"
                        title="View image"
                      >
                        View
                      </button>
                      {!config.disabled && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFile(index);
                          }}
                          className="flex-shrink-0 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 rounded-md px-3 py-1.5 text-xs font-medium hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
                          title="Remove image"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    {/* Grid layout for multiple files */}
                    <div 
                      className="relative aspect-square overflow-hidden bg-gray-100 dark:bg-gray-900 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewImage(index);
                      }}
                    >
                      <img
                        src={preview}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                      {/* Overlay on hover */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
                      {/* View button overlay */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <div className="bg-white/90 dark:bg-gray-800/90 rounded-full p-2 shadow-lg">
                          <svg className="w-5 h-5 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                    {/* Action buttons */}
                    <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      {!config.disabled && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFile(index);
                          }}
                          className="bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold hover:bg-red-600 hover:scale-110 transition-all duration-200 shadow-lg"
                          title="Remove image"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                    {/* Image number badge */}
                    <div className="absolute top-2 left-2 bg-black/60 text-white text-xs font-semibold px-1.5 py-0.5 rounded backdrop-blur-sm">
                      #{index + 1}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2 animate-in fade-in slide-in-from-top-2 duration-200">
          <svg className="w-5 h-5 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="flex-1">
            <p className="text-sm font-medium text-red-700 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      {config.helperText && !error && (
        <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-start gap-2">
          <svg className="w-5 h-5 text-blue-500 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-blue-700 dark:text-blue-300">{config.helperText}</p>
        </div>
      )}

      {/* Image View Modal */}
      {isViewModalOpen && viewingImageIndex !== null && previews[viewingImageIndex] && (
        <div 
          className="fixed inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-75 dark:bg-opacity-85"
          onClick={handleCloseViewModal}
        >
          <div 
            className="relative w-[90vw] h-[90vh] max-w-7xl max-h-[90vh] bg-white dark:bg-gray-800 rounded-lg shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                  Image {viewingImageIndex + 1} {previews.length > 1 && `of ${previews.length}`}
                </h3>
              </div>
              <div className="flex items-center gap-2 ml-4">
                {/* Zoom Controls */}
                <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg px-2 py-1">
                  <button
                    onClick={handleZoomOut}
                    disabled={zoomLevel <= 25}
                    className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-70 disabled:cursor-not-allowed transition-colors"
                    title="Zoom Out"
                  >
                    <svg className="w-4 h-4 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                    </svg>
                  </button>
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300 min-w-[3rem] text-center">
                    {zoomLevel}%
                  </span>
                  <button
                    onClick={handleZoomIn}
                    disabled={zoomLevel >= 300}
                    className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-70 disabled:cursor-not-allowed transition-colors"
                    title="Zoom In"
                  >
                    <svg className="w-4 h-4 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                    </svg>
                  </button>
                  <button
                    onClick={handleResetZoom}
                    className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors ml-1"
                    title="Reset Zoom"
                  >
                    <svg className="w-4 h-4 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                </div>
                {/* Navigation buttons for multiple images */}
                {previews.length > 1 && (
                  <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg px-1">
                    <button
                      onClick={handlePreviousImage}
                      disabled={viewingImageIndex === 0}
                      className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-70 disabled:cursor-not-allowed transition-colors"
                      title="Previous image"
                    >
                      <svg className="w-4 h-4 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={handleNextImage}
                      disabled={viewingImageIndex === previews.length - 1}
                      className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-70 disabled:cursor-not-allowed transition-colors"
                      title="Next image"
                    >
                      <svg className="w-4 h-4 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                )}
                {/* Close Button */}
                <button
                  onClick={handleCloseViewModal}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  title="Close"
                >
                  <svg className="w-5 h-5 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content - Scrollable with Zoom */}
            <div className="flex-1 overflow-auto p-4 bg-gray-50 dark:bg-gray-900">
              <div className="flex items-center justify-center h-full w-full" style={{ transform: `scale(${zoomLevel / 100})`, transformOrigin: 'center' }}>
                <img 
                  src={previews[viewingImageIndex]} 
                  alt={`Image ${viewingImageIndex + 1}`} 
                  className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
